// =============================================================================
// features/conversations/hooks/useAttachMediaAsDocument.ts — "Anexar aos
// documentos" a mídia de uma mensagem do live chat (F46-S11).
//
// docs/planejamento-documentos-anexos.md §6.1 ("Live chat → ação 'Anexar aos
// documentos'"). Reusa o cliente HTTP e o gate já existentes de
// features/documents (F46-S04/S08/S19) — NUNCA reimplementa a chamada
// POST /api/documents nem a checagem de flag+permissão daquele módulo.
//
// Contrato do backend (F46-S23, apps/api/src/modules/documents/service.ts):
//   - `POST /api/documents` com `messageId` (sem `key`) resolve a key/mime/
//     tamanho a partir da MENSAGEM — o cliente nunca vê nem envia a key
//     opaca do storage. Sem re-upload.
//   - 409 quando a mensagem já virou documento (`uq_documents_message`).
//   - 422 quando a mensagem é anterior ao suporte de `media_storage_key`
//     (migration 0143, 04/09/2026) — falha PERMANENTE, sem retry possível.
//
// Idempotency-Key: um UUID novo por tentativa de submit (mesmo padrão de
// features/documents/hooks.ts `runDocumentUploadStep`) — nunca derivado do
// `messageId`, porque a idempotência do backend faz replay puro pela key
// (ignora o corpo); uma key fixa por mensagem devolveria silenciosamente o
// resultado da primeira tentativa mesmo que o agente tenha corrigido o nome
// de exibição num reenvio. A proteção contra duplicata de verdade é o índice
// único `uq_documents_message` do servidor (409 tratado abaixo), não a
// Idempotency-Key.
// =============================================================================

import type { Document } from '@elemento/shared-schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../lib/api';
import { attachDocument } from '../../documents/api';
import {
  documentsQueryKeys,
  useDocumentsGate,
  useDocumentTypesCatalog,
} from '../../documents/hooks';
import { conversationKeys } from '../queries';

export { useDocumentsGate, useDocumentTypesCatalog };

// ---------------------------------------------------------------------------
// Erros tipados — a UI trata cada um de forma diferente (nota do slot: 409
// tratado com mensagem clara; 422 é falha permanente, sem retry).
// ---------------------------------------------------------------------------

/**
 * 409 — a mensagem já virou documento. `existingDocumentId` vem de um parse
 * best-effort da mensagem de erro do servidor (formato estável, testado em
 * apps/api/src/modules/documents/__tests__/documents.test.ts) — usado só
 * como fallback de UX enquanto `attachedDocumentId` (F46-S29) ainda não está
 * em `main`; quando estiver, o indicador persistente já resolve isso sem
 * precisar deste parse.
 */
export class AttachAlreadyDoneError extends Error {
  readonly existingDocumentId: string | null;
  constructor(message: string, existingDocumentId: string | null) {
    super(message);
    this.name = 'AttachAlreadyDoneError';
    this.existingDocumentId = existingDocumentId;
  }
}

/** 422 — mídia anterior ao suporte de `media_storage_key` (04/09/2026). Não
 * há backfill possível: nunca oferecer "tentar novamente" para este erro. */
export class AttachMediaTooOldError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AttachMediaTooOldError';
  }
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Extrai o uuid do documento existente da mensagem de 409 do servidor
 * ("Esta mensagem já foi anexada como o documento {id}."). `null` se o
 * formato mudar — a UI cai de volta em "já anexado, sem link direto". */
export function parseExistingDocumentId(message: string): string | null {
  const match = UUID_RE.exec(message);
  return match ? match[0] : null;
}

function toAttachError(err: unknown): Error {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return new AttachAlreadyDoneError(err.message, parseExistingDocumentId(err.message));
    }
    if (err.status === 422) {
      return new AttachMediaTooOldError(err.message);
    }
  }
  return err instanceof Error ? err : new Error('Não foi possível anexar o documento.');
}

// ---------------------------------------------------------------------------
// Mutação
// ---------------------------------------------------------------------------

export interface AttachMediaAsDocumentInput {
  messageId: string;
  conversationId: string;
  leadId: string;
  documentTypeId: string;
  /** Nome de exibição editável pelo agente. Vazio/undefined → o servidor
   * cai no fallback de `original_filename` (F46-S31) — nunca sobrescreve a
   * proveniência. */
  displayName?: string | undefined;
}

/**
 * Mutação única para "Anexar aos documentos" a partir do live chat.
 *
 * Nota de montagem (DoD do slot): esta função deve ser chamada UMA vez por
 * fluxo de anexo (ver `MessageBubble.tsx`, que monta o hook uma vez por
 * mensagem — o dispatcher já rendeizado uma vez por mensagem pelo painel —
 * e passa a ação para `MediaBubble` via prop), nunca duplicada dentro de
 * variantes irmãs de bolha (ImageBubble/VideoBubble/etc. dentro de
 * MediaBubble.tsx) — o precedente do live chat é o contador duplicado por
 * hook de socket montado duas vezes (ver useConversationSocket.ts).
 */
export function useAttachMediaAsDocument() {
  const queryClient = useQueryClient();

  return useMutation<Document, Error, AttachMediaAsDocumentInput>({
    mutationFn: async (input) => {
      const idempotencyKey = crypto.randomUUID();
      try {
        return await attachDocument(
          {
            messageId: input.messageId,
            leadId: input.leadId,
            documentTypeId: input.documentTypeId,
            ...(input.displayName?.trim() ? { displayName: input.displayName.trim() } : {}),
          },
          idempotencyKey,
        );
      } catch (err) {
        throw toAttachError(err);
      }
    },
    onSuccess: (_document, variables) => {
      // Central do CRM (docs/planejamento-documentos-anexos.md §6.1 "o
      // documento tem que aparecer no CRM também") + checklist derivado.
      void queryClient.invalidateQueries({ queryKey: documentsQueryKeys.lead(variables.leadId) });
      void queryClient.invalidateQueries({ queryKey: documentsQueryKeys.all });
      // Mensagens da conversa — refresca `attachedDocumentId` (F46-S29)
      // assim que o campo existir em `main`; até lá, invalidação inofensiva.
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.messages(variables.conversationId),
      });
    },
  });
}
